import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { differenceInCalendarDays } from "date-fns";
import { formatDisplayDate, parseDateInput } from "@/utils/formatDisplayDate";
import { ArrowRight, Check, ChevronLeft, ChevronRight, UserRound } from "lucide-react";
import { Text, Heading } from "@/components/Typography";
import { Button } from "@/components/Button";
import { FormattedText } from "@/components/FormattedText";
import type { PublicAdvisorSummary, PublicProgramCardOut } from "@/services/api";
import { formatCents } from "@/types/payments";
import { ENROLLED_GREEN } from "./enrolledStyles";

// The subset of fields FeaturedCard actually uses — satisfied by both
// PublicProgramCardOut (Explore page cards) and PublicProgramPreviewOut (the
// public program detail page), so the same hero card renders in both places
// without either schema needing to shed/gain fields for the other's sake.
export interface FeaturedCardProgram {
  externalId: string;
  name: string;
  description: string | null;
  isFeatured: boolean;
  /** Optional so callers with no session context can omit it entirely. */
  isEnrolled?: boolean;
  startsAt: string | null;
  durationWeeks: number;
  slotsLeft: number;
  maxSlots: number;
  leadAdvisor: PublicAdvisorSummary | null;
  /** Integer cents. `null`/`undefined` renders as "Price TBD"; 0 as "Free". */
  priceCents?: number | null;
  /**
   * Optional so the existing callers -- and any future one built from a payload
   * that predates two program types -- keep working unchanged. Absent or
   * "EXPERT" renders exactly as before.
   */
  programType?: "EXPERT" | "PEER_ADVISORY";
  /**
   * Peer-only meta the hero shows in place of the Expert equivalents: the group
   * size band instead of a slot count, and the application deadline instead of
   * the kick-off date.
   */
  peer?: {
    minMembers: number | null;
    maxMembers: number | null;
    applicationDeadline: string | null;
  } | null;
}

export interface FeaturedProgramCarouselProps {
  programs: PublicProgramCardOut[];
  /** See FeaturedCard's prop of the same name. */
  onViewDetails?: (externalId: string) => void;
}

// Fixed card height so the carousel never resizes as it pages through programs
// with different description/name lengths — matches Figma node 177:577 (420px).
const CARD_HEIGHT = 420;

/** How long each featured program stays on screen before auto-advancing. */
const AUTO_ADVANCE_MS = 5000;


export const FeaturedCard = ({
  program,
  actionLabel = "View Details",
  actionDisabled = false,
  actionSubscribed = false,
  hideAction = false,
  onAction,
  onViewDetails,
}: {
  program: FeaturedCardProgram;
  actionLabel?: string;
  /** Disables the action button — e.g. while an enrollment request is in flight. */
  actionDisabled?: boolean;
  /**
   * The viewer is already enrolled: render the action as a green status chip
   * instead of a button — same footprint, no click target, full opacity.
   *
   * Deliberately not `actionDisabled`: a dimmed button reads as "temporarily
   * unavailable, try again", when the truth is that this program is already
   * theirs and there is nothing here left to do.
   */
  actionSubscribed?: boolean;
  /**
   * Hides the action button entirely — e.g. an advisor viewing a program's
   * detail page from the Expert workspace: enrolling as an achiever isn't a
   * thing they can do, and co-advisor access is invite-only (granted by the
   * program's lead expert), not self-serve, so there's no button for them here.
   */
  hideAction?: boolean;
  /** Defaults to navigating to the program's public detail page. */
  onAction?: () => void;
  /**
   * Renders the program's detail view in place of the browsing UI instead of
   * navigating to /join/:externalId (Explore page). Takes priority over the
   * navigate-away default but not over an explicit `onAction` — the detail
   * view's own hero card passes `onAction` so its button enrolls rather than
   * re-opening the view it's already inside.
   */
  onViewDetails?: (externalId: string) => void;
}) => {
  const navigate = useNavigate();
  const expert = program.leadAdvisor;
  const isPeer = program.programType === "PEER_ADVISORY";
  const peer = program.peer ?? null;

  // What closes, and when, differs by type: an Expert Program closes when its
  // cohort starts; a peer group closes on its application deadline, which is
  // independent of kick-off.
  const closesAt = isPeer ? (peer?.applicationDeadline ?? null) : program.startsAt;
  const closesDate = parseDateInput(closesAt);
  const rawDaysToEnroll = closesDate
    ? differenceInCalendarDays(closesDate, new Date())
    : null;
  // A date already in the past (common with seed/test data) isn't "0 days left"
  // — it has effectively closed, so the countdown is simply not shown rather
  // than displaying a misleading "0".
  const daysToEnroll = rawDaysToEnroll !== null && rawDaysToEnroll >= 0 ? rawDaysToEnroll : null;
  // No ceiling means no scarcity, not zero seats — a peer facilitator may not
  // have set a size band yet.
  const hasCeiling = isPeer ? peer?.maxMembers != null : true;
  const isFull = hasCeiling && program.slotsLeft <= 0;

  return (
    <div
      className="relative overflow-hidden rounded-[24px] bg-[var(--color-prog-page-dark-bg,#0c1633)]"
      style={{ height: CARD_HEIGHT }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(154deg, rgba(64,81,181,0.4) 0%, rgba(124,58,237,0.2) 50%, rgba(0,0,0,0) 100%)",
        }}
      />

      <div className="relative flex h-full flex-col md:flex-row">
        {/* No max-width cap: the copy column flexes right up to the portrait so
            there's no dead gutter between the text and the lead expert image. */}
        <div className="flex-1 min-w-0 p-8 flex flex-col items-start overflow-hidden">
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <Text
              as="span"
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-semibold tracking-[0.12px]"
              style={{
                background: isPeer ? "var(--prog-status-live-bg)" : "var(--prog-session-1on1-bg)",
                borderColor: isPeer ? "var(--prog-status-live-text)" : "var(--prog-session-1on1-text)",
                color: isPeer ? "var(--prog-status-live-text)" : "var(--prog-session-1on1-text)",
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: isPeer ? "var(--prog-status-live-text)" : "var(--prog-session-1on1-text)",
                }}
              />
              {isPeer ? "Peer Program" : "Expert Program"}
            </Text>
            {program.isFeatured && (
              <Text
                as="span"
                className="inline-flex items-center rounded-full border px-[13px] py-[5px] text-[10px] font-semibold tracking-[0.12px]"
                style={{
                  background: "rgba(255,185,0,0.15)",
                  borderColor: "rgba(255,185,0,0.25)",
                  color: "#ffd230",
                }}
              >
                Featured
              </Text>
            )}
            {/* Gated on actionSubscribed, not program.isEnrolled: this card is
                also the Explore carousel, where a browsing list should not be
                annotated with per-viewer state. Only the detail view passes
                actionSubscribed, so the pill appears there and nowhere else. */}
            {actionSubscribed && (
              <Text
                as="span"
                className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[10px] font-semibold tracking-[0.12px]"
                style={{
                  background: ENROLLED_GREEN.bg,
                  borderColor: ENROLLED_GREEN.border,
                  color: ENROLLED_GREEN.text,
                }}
              >
                <Check className="h-3 w-3" aria-hidden="true" />
                Subscribed
              </Text>
            )}
          </div>

          <Heading
            as="h2"
            className="mt-3 text-[22px] font-bold leading-[28px] tracking-[-0.41px] text-white shrink-0 line-clamp-2"
          >
            {program.name}
          </Heading>

          {expert && (
            <Text
              as="p"
              className="mt-1.5 text-[14px] font-semibold leading-5 tracking-[-0.15px] text-white shrink-0 line-clamp-1"
            >
              By {expert.fullName}
              {expert.currentTitle ? `, ${expert.currentTitle}` : ""}
            </Text>
          )}

          {/* Fixed-height slot (3 lines) regardless of whether a description
              renders, so "Next cohort" / "Availability" below never shift. */}
          <div className="mt-3 h-[69px] shrink-0">
            {program.description && program.description !== program.name && (
              <Text as="p" className="text-[14px] leading-[23px] tracking-[-0.15px] text-white/65 line-clamp-3">
                <FormattedText text={program.description} />
              </Text>
            )}
          </div>

          <div className="mt-auto pt-5 flex items-start gap-8 shrink-0">
            <div>
              <Text as="p" className="text-[12px] uppercase tracking-[0.6px] text-white/40">
                {isPeer ? "Applications close" : "Next cohort"}
              </Text>
              <Text as="p" className="mt-0.5 text-[14px] font-semibold leading-5 tracking-[-0.15px] text-white">
                {closesAt
                  ? formatDisplayDate(closesAt, { variant: "date", length: "long" })
                  : isPeer
                    ? "Open"
                    : "Date TBD"}
              </Text>
              {daysToEnroll !== null && (
                <Text as="p" className="mt-0.5 text-[10px] font-medium tracking-[0.12px]" style={{ color: "#ffd230" }}>
                  {daysToEnroll} days left to {isPeer ? "apply" : "enroll"}
                </Text>
              )}
            </div>
            <div>
              <Text as="p" className="text-[12px] uppercase tracking-[0.6px] text-white/40">
                {isPeer ? "Group size" : "Availability"}
              </Text>
              <Text as="p" className="mt-0.5 text-[14px] font-semibold leading-5 tracking-[-0.15px] text-white">
                {/* A peer group's band is the headline number: a small,
                    consistent room is the point of the format, whereas an
                    Expert Program's is how many seats are left to sell. */}
                {isPeer
                  ? peer?.minMembers != null && peer?.maxMembers != null
                    ? `${peer.minMembers}–${peer.maxMembers} members`
                    : "Small group"
                  : isFull
                    ? "Full"
                    : `${program.slotsLeft} of ${program.maxSlots} spots left`}
              </Text>
            </div>
            <div>
              <Text as="p" className="text-[12px] uppercase tracking-[0.6px] text-white/40">
                {isPeer ? "Joining" : "Price"}
              </Text>
              <Text as="p" className="mt-0.5 text-[14px] font-semibold leading-5 tracking-[-0.15px] text-white">
                {isPeer
                  ? "By application"
                  : program.priceCents == null
                    ? "Price TBD"
                    : program.priceCents === 0
                      ? "Free"
                      : formatCents(program.priceCents)}
              </Text>
            </div>
          </div>

          {/* Enrolled: a green chip in the button's place. Not a <button> at
              all -- there is no action, so there should be no click target and
              no focus stop, and `disabled` would only grey out a truthful
              status. */}
          {!hideAction && actionSubscribed && (
            <span
              className="mt-6 inline-flex shrink-0 items-center gap-2 rounded-[16px] border px-6 py-3 text-[14px] font-semibold"
              style={{
                background: ENROLLED_GREEN.bg,
                borderColor: ENROLLED_GREEN.border,
                color: ENROLLED_GREEN.text,
              }}
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              {actionLabel}
            </span>
          )}

          {!hideAction && !actionSubscribed && (
            <Button
              variant="primaryGradient"
              size="sm"
              rightIcon={<ArrowRight className="w-4 h-4" />}
              className="mt-6 rounded-[16px] px-6 py-3 text-[14px] font-semibold shrink-0 text-white"
              disabled={actionDisabled}
              // Explore renders the detail view in place (onViewDetails) so the
              // user never leaves the page. Elsewhere the default still links out
              // to /join/:programId (PublicProgramLandingPage), which has its own
              // Enroll button; that page passes onAction to go straight to
              // enrollment rather than linking to itself.
              onClick={
                onAction ??
                (onViewDetails
                  ? () => onViewDetails(program.externalId)
                  : () => navigate(`/join/${program.externalId}`))
              }
            >
              {actionLabel}
            </Button>
          )}
        </div>

        {/* Figma node 177:618 — the arch photo (fully rounded top) sits flush
            against the card's right edge, full height. A narrow gradient strip
            over its left edge (node 177:621) blends the photo into the card's
            dark background instead of a hard seam. */}
        {/* Sized to the portrait itself (was 410px around a 296px photo, which
            left a ~114px empty gutter between the copy and the image). */}
        <div className="hidden md:block relative w-[300px] h-full shrink-0 overflow-hidden">
          <div className="absolute right-0 top-0 h-full w-[296px] rounded-t-full overflow-hidden bg-white/5">
            {expert?.profilePictureUrl ? (
              <img
                src={expert.profilePictureUrl}
                alt={expert.fullName ?? ""}
                className="absolute inset-0 w-full h-full object-cover grayscale"
              />
            ) : (
              // Generic silhouette rather than an initial — a lone letter on a
              // large portrait slot reads as a bug, not a placeholder.
              <div className="absolute inset-0 flex items-end justify-center">
                <UserRound className="w-[45%] h-[45%] mb-[18%] text-white/25" strokeWidth={1.5} />
              </div>
            )}
            <div
              className="absolute inset-y-0 left-0 w-16"
              style={{
                background: "linear-gradient(90deg, rgba(12,22,51,0.6) 0%, rgba(12,22,51,0) 100%)",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Hero carousel for is_featured programs on the Explore page — Figma node 177:577.
 * Every card renders at a fixed height (CARD_HEIGHT) so paging between featured
 * programs never shifts page layout. Renders one FeaturedCard at a time with
 * prev/next controls when there is more than one featured program; a single
 * featured program renders with no controls.
 */
export const FeaturedProgramCarousel = ({ programs, onViewDetails }: FeaturedProgramCarouselProps) => {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const count = programs.length;
  const showControls = count > 1;

  const goTo = useCallback((next: number) => setIndex(((next % count) + count) % count), [count]);

  // Auto-advance. Paused on hover/focus so the slide doesn't change out from
  // under someone reaching for the arrows (which only appear on hover) or
  // tabbing through the dots. The timer resets whenever `index` changes, so a
  // manual arrow/dot click restarts the full interval rather than leaving a
  // partially-elapsed one that jumps again immediately.
  useEffect(() => {
    if (!showControls || isPaused) return;
    const timer = setTimeout(() => goTo(index + 1), AUTO_ADVANCE_MS);
    return () => clearTimeout(timer);
  }, [index, isPaused, showControls, goTo]);

  if (count === 0) return null;

  // Clamp rather than index directly: a refetch returning fewer programs can
  // leave `index` past the end, which would otherwise unhighlight every dot.
  const activeIndex = index % count;

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      <div className="group relative overflow-hidden rounded-[24px]">
        {/* Sliding track: all cards sit side by side and the whole strip
            translates, so advancing is a smooth slide instead of a hard cut. */}
        <div
          className="flex transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {programs.map((p) => (
            <div key={p.externalId} className="w-full shrink-0">
              <FeaturedCard program={p} onViewDetails={onViewDetails} />
            </div>
          ))}
        </div>

        {showControls && (
          <>
            <button
              type="button"
              aria-label="Previous featured program"
              onClick={() => goTo(index - 1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              aria-label="Next featured program"
              onClick={() => goTo(index + 1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity hover:bg-black/70"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {showControls && (
        <div className="mt-3 flex items-center justify-center gap-1.5">
          {programs.map((p, i) => (
            <button
              key={p.externalId}
              type="button"
              aria-label={`Go to featured program ${i + 1}`}
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === activeIndex ? "w-5 bg-primary-purple" : "w-1.5 bg-secondary-300 dark:bg-white/20"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FeaturedProgramCarousel;
